import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      main: '#0A2647',
      light: '#1a3b5c',
      dark: '#061525',
      contrastText: '#ffffff'
    },
    secondary: {
      main: '#39FF14',
      light: '#5cff3d',
      dark: '#2ecc11',
      contrastText: '#0A2647'
    },
    background: {
      default: '#f0f2f5',
      paper: '#ffffff'
    },
    text: {
      primary: '#1a1a2e',
      secondary: '#4a4a6a'
    },
    success: {
      main: '#2ecc71',
      light: '#58d68d'
    },
    error: {
      main: '#e74c3c',
      light: '#ec7063'
    },
    warning: {
      main: '#f39c12',
      light: '#f5b041'
    },
    info: {
      main: '#3498db',
      light: '#5dade2'
    }
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", sans-serif',
    h1: { 
      fontWeight: 700,
      fontSize: '2.5rem'
    },
    h2: { fontWeight: 600 },
    h3: { fontWeight: 600 },
    h4: { fontWeight: 600 },
    h5: { 
      fontWeight: 600,
      fontSize: '1.25rem'
    },
    h6: { 
      fontWeight: 600,
      fontSize: '1.1rem'
    },
    subtitle1: {
      fontWeight: 500
    },
    button: { 
      textTransform: 'none', 
      fontWeight: 600 
    }
  },
  shape: {
    borderRadius: 12
  },
  shadows: [
    'none',
    '0 2px 8px rgba(0,0,0,0.04)',
    '0 4px 16px rgba(0,0,0,0.06)',
    '0 8px 24px rgba(0,0,0,0.08)',
    '0 12px 32px rgba(0,0,0,0.10)',
    '0 16px 40px rgba(0,0,0,0.12)',
    '0 20px 48px rgba(0,0,0,0.14)',
    '0 24px 56px rgba(0,0,0,0.16)',
    '0 28px 64px rgba(0,0,0,0.18)',
    '0 32px 72px rgba(0,0,0,0.20)',
    '0 36px 80px rgba(0,0,0,0.22)',
    '0 40px 88px rgba(0,0,0,0.24)',
    '0 44px 96px rgba(0,0,0,0.26)',
    '0 48px 104px rgba(0,0,0,0.28)',
    '0 52px 112px rgba(0,0,0,0.30)',
    '0 56px 120px rgba(0,0,0,0.32)',
    '0 60px 128px rgba(0,0,0,0.34)',
    '0 64px 136px rgba(0,0,0,0.36)',
    '0 68px 144px rgba(0,0,0,0.38)',
    '0 72px 152px rgba(0,0,0,0.40)',
    '0 76px 160px rgba(0,0,0,0.42)',
    '0 80px 168px rgba(0,0,0,0.44)',
    '0 84px 176px rgba(0,0,0,0.46)',
    '0 88px 184px rgba(0,0,0,0.48)',
    '0 92px 192px rgba(0,0,0,0.50)'
  ],
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
          borderRadius: 16,
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 8px 30px rgba(0,0,0,0.10)',
            transform: 'translateY(-2px)'
          }
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '8px 24px',
          transition: 'all 0.3s ease',
          fontWeight: 600,
          fontSize: '0.875rem'
        },
        contained: {
          background: 'linear-gradient(135deg, #0A2647 0%, #1a3b5c 100%)',
          boxShadow: '0 4px 15px rgba(10,38,71,0.2)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(10,38,71,0.3)',
            background: 'linear-gradient(135deg, #0A2647 0%, #1a3b5c 100%)'
          }
        },
        containedSecondary: {
          background: 'linear-gradient(135deg, #39FF14 0%, #2ecc11 100%)',
          color: '#0A2647',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(57,255,20,0.3)'
          }
        },
        outlined: {
          borderColor: '#0A2647',
          color: '#0A2647',
          '&:hover': {
            background: 'rgba(10,38,71,0.05)',
            borderColor: '#0A2647',
            transform: 'translateY(-2px)'
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 20px rgba(0,0,0,0.06)'
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          color: '#0A2647',
          backgroundColor: 'rgba(10, 38, 71, 0.04)'
        },
        root: {
          borderBottom: '1px solid rgba(0,0,0,0.06)'
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500
        },
        colorSuccess: {
          background: 'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)',
          color: '#ffffff'
        },
        colorError: {
          background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
          color: '#ffffff'
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            '&:hover fieldset': {
              borderColor: '#0A2647'
            },
            '&.Mui-focused fieldset': {
              borderColor: '#0A2647',
              borderWidth: 2
            }
          }
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 10
        }
      }
    }
  }
});

export default theme;
